import {
  ResultadoDeCompletarContenedoresSchema,
  ResultadoDeCambioDeFechaDeContenedoresSchema,
  type CambioDeFechaDeContenedores,
  AlertasDeSeguimientoSchema,
  CatalogosDeSeguimientoSchema,
  ConsultaSeguimientoSchema,
  ExportacionDetalleSchema,
  FiltrosDeSeguimientoConsultaSchema,
  FiltrosDeSeguimientoSchema,
  ListaDePrefijosSchema,
  LogisticaDeExportacionSchema,
  MapaDeSeguimientoSchema,
  OpcionesDeColumnaSchema,
  PaginaDeContenedoresSchema,
  PaginaDeSeguimientoSchema,
  ResultadoDeEliminarExportacionesSchema,
  ResultadoDelLoteSchema,
  ResultadoDeSeparacionSchema,
  SeparacionDeExportacionSchema,
  type ConsultaSeguimiento,
  type EdicionDeExportacion,
  type EliminarExportaciones,
  type FiltrosDeSeguimientoConsulta,
  type GuardarPrefijos,
  type LoteDeCambios,
} from '@cnc/contracts';
import {keepPreviousData, useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {pedir} from './cliente';
import {refrescarDatos} from './refrescar';
import {aParametros, desdeParametros} from './consulta';

const POR_DEFECTO: Record<string, unknown> = ConsultaSeguimientoSchema.parse({});

export const consultaDeSeguimientoDesdeParametros = (parametros: URLSearchParams) =>
  desdeParametros(parametros, ConsultaSeguimientoSchema);
export const parametrosDeSeguimiento = (consulta: Record<string, unknown>) => aParametros(consulta, POR_DEFECTO);
export const soloFiltrosDeSeguimiento = (consulta: Record<string, unknown>): FiltrosDeSeguimientoConsulta =>
  FiltrosDeSeguimientoConsultaSchema.parse(consulta);

const ruta = (base: string, consulta: Record<string, unknown>) => {
  const cadena = parametrosDeSeguimiento(consulta).toString();
  return `${base}${cadena ? `?${cadena}` : ''}`;
};

export function useSeguimiento(consulta: ConsultaSeguimiento) {
  const direccion = ruta('seguimiento/exportaciones', consulta);
  return useQuery({
    queryKey: ['seguimiento', 'lista', direccion],
    queryFn: () => pedir(direccion, PaginaDeSeguimientoSchema),
    placeholderData: keepPreviousData,
  });
}

export function useFiltrosDeSeguimiento(filtros: FiltrosDeSeguimientoConsulta) {
  const direccion = ruta('seguimiento/filtros', filtros);
  return useQuery({
    queryKey: ['seguimiento', 'filtros', direccion],
    queryFn: () => pedir(direccion, FiltrosDeSeguimientoSchema),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

// Valores de una columna para su filtro por columna; solo se pide al abrir la lista.
export function useOpcionesDeColumnaDeSeguimiento(
  filtros: FiltrosDeSeguimientoConsulta,
  columna: string,
  contiene: string,
  habilitado: boolean,
) {
  const direccion = ruta('seguimiento/opciones-de-columna', {...filtros, columna, contiene: contiene || undefined});
  return useQuery({
    queryKey: ['seguimiento', 'opciones-de-columna', direccion],
    queryFn: () => pedir(direccion, OpcionesDeColumnaSchema),
    enabled: habilitado,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useAlertasDeSeguimiento(filtros: FiltrosDeSeguimientoConsulta, activo = true) {
  const direccion = ruta('seguimiento/alertas', filtros);
  return useQuery({
    enabled: activo,
    queryKey: ['seguimiento', 'alertas', direccion],
    queryFn: () => pedir(direccion, AlertasDeSeguimientoSchema),
    placeholderData: keepPreviousData,
    refetchInterval: 5 * 60_000,
  });
}

export function useMapaDeSeguimiento(filtros: FiltrosDeSeguimientoConsulta) {
  const direccion = ruta('seguimiento/mapa', filtros);
  return useQuery({
    queryKey: ['seguimiento', 'mapa', direccion],
    queryFn: () => pedir(direccion, MapaDeSeguimientoSchema),
    placeholderData: keepPreviousData,
  });
}

export function usePrefijos() {
  return useQuery({
    queryKey: ['seguimiento', 'prefijos'],
    queryFn: () => pedir('seguimiento/prefijos', ListaDePrefijosSchema),
  });
}

export function useGuardarPrefijos() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (cuerpo: GuardarPrefijos) =>
      pedir('seguimiento/prefijos', ListaDePrefijosSchema, {
        method: 'PUT',
        headers: {'content-type': 'application/json'},
        body: JSON.stringify(cuerpo),
      }),
    // Cambia el exportador de muchas filas: se refresca todo lo que depende de el.
    onSuccess: () => refrescarDatos(cliente),
  });
}

// Direccion del Excel "para editar" con los mismos filtros y orden de la tabla.
export const rutaExcelDeSeguimiento = (consulta: Record<string, unknown>) => {
  const sinPaginacion = {...consulta, pagina: undefined, porPagina: undefined};
  const cadena = parametrosDeSeguimiento(sinPaginacion).toString();
  return `/api/seguimiento/exportaciones/excel${cadena ? `?${cadena}` : ''}`;
};

// Cambios en muchas exportaciones (o en una sola celda): previsualizar o aplicar.
export function useLoteDeCambios() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (cuerpo: LoteDeCambios) =>
      pedir('seguimiento/exportaciones/lote', ResultadoDelLoteSchema, {
        method: 'POST',
        headers: {'content-type': 'application/json'},
        body: JSON.stringify(cuerpo),
      }),
    onSuccess: async (_resultado, cuerpo) => {
      if (cuerpo.modo === 'aplicar') await refrescarDatos(cliente);
    },
  });
}

// Eliminar exportaciones elegidas o todas las filtradas (solo administradores).
export function useEliminarExportaciones() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (cuerpo: EliminarExportaciones) =>
      pedir('seguimiento/exportaciones/eliminar', ResultadoDeEliminarExportacionesSchema, {
        method: 'POST',
        headers: {'content-type': 'application/json'},
        body: JSON.stringify(cuerpo),
      }),
    onSuccess: async (_resultado, cuerpo) => {
      if (cuerpo.modo === 'eliminar') await refrescarDatos(cliente);
    },
  });
}

export function useExportacion(codigo: string) {
  return useQuery({
    queryKey: ['seguimiento', 'exportacion', codigo],
    queryFn: () => pedir(`seguimiento/exportaciones/${encodeURIComponent(codigo)}`, ExportacionDetalleSchema),
    retry: false,
  });
}

// Todos los contenedores con logistica, con los filtros de la direccion.
export function useContenedores(parametros: URLSearchParams) {
  const cadena = parametros.toString();
  return useQuery({
    queryKey: ['seguimiento', 'contenedores', cadena],
    queryFn: () => pedir(`seguimiento/contenedores${cadena ? `?${cadena}` : ''}`, PaginaDeContenedoresSchema),
    placeholderData: keepPreviousData,
  });
}

// Logistica por contenedor (Seguimiento Concentrados) de una exportacion.
export function useLogisticaDeExportacion(codigo: string) {
  return useQuery({
    queryKey: ['seguimiento', 'logistica', codigo],
    queryFn: () =>
      pedir(`seguimiento/exportaciones/${encodeURIComponent(codigo)}/logistica`, LogisticaDeExportacionSchema),
    retry: false,
  });
}

export function useCatalogosDeSeguimiento() {
  return useQuery({
    queryKey: ['seguimiento', 'catalogos'],
    queryFn: () => pedir('seguimiento/catalogos', CatalogosDeSeguimientoSchema),
    staleTime: 60_000,
  });
}

export function useEditarExportacion(codigo: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (cambios: EdicionDeExportacion) =>
      pedir(`seguimiento/exportaciones/${encodeURIComponent(codigo)}`, ExportacionDetalleSchema, {
        method: 'PATCH',
        headers: {'content-type': 'application/json'},
        body: JSON.stringify(cambios),
      }),
    onSuccess: async detalle => {
      cliente.setQueryData(['seguimiento', 'exportacion', detalle.codigo], detalle);
      await refrescarDatos(cliente);
    },
  });
}

// Reparte los Big Bags sin contenedor en los contenedores de Seguimiento Concentrados (decision 0053).
export function useCompletarContenedores() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (modo: 'revisar' | 'aplicar') =>
      pedir('seguimiento/contenedores/completar', ResultadoDeCompletarContenedoresSchema, {
        method: 'POST',
        headers: {'content-type': 'application/json'},
        body: JSON.stringify({modo}),
      }),
    onSuccess: async r => {
      if (r.aplicado) await refrescarDatos(cliente);
    },
  });
}

// Corregir una fecha (entrada a puerto, zarpe...) de varios contenedores (23/10/2026).
export function useCambiarFechaDeContenedores() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (pedido: CambioDeFechaDeContenedores) =>
      pedir('seguimiento/contenedores/fecha', ResultadoDeCambioDeFechaDeContenedoresSchema, {
        method: 'POST',
        headers: {'content-type': 'application/json'},
        body: JSON.stringify(pedido),
      }),
    onSuccess: async r => {
      if (r.aplicado) await refrescarDatos(cliente);
    },
  });
}

// Separar una exportacion en A y B (decision 0071).
export function useSeparacion(codigo: string, activa: boolean) {
  return useQuery({
    queryKey: ['seguimiento', 'separacion', codigo],
    queryFn: () =>
      pedir(`seguimiento/exportaciones/${encodeURIComponent(codigo)}/separacion`, SeparacionDeExportacionSchema),
    enabled: activa,
  });
}

export function useSeparar(codigo: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (contenedoresB: number[]) =>
      pedir(`seguimiento/exportaciones/${encodeURIComponent(codigo)}/separar`, ResultadoDeSeparacionSchema, {
        method: 'POST',
        headers: {'content-type': 'application/json'},
        body: JSON.stringify({contenedoresB}),
      }),
    onSuccess: () => refrescarDatos(cliente),
  });
}
